import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImagePreviewPopupComponent } from 'src/app/shared/image-preview-popup/image-preview-popup.component';
import { Context } from 'src/app/shared/models/context';
import { Message, PrivilegeType } from 'src/app/shared/models/domain';
import { DossierKYC } from 'src/app/shared/models/dossier';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { DossierKycService } from 'src/app/shared/services/dossier-kyc.service';

@Component({
  selector: 'app-employee-kyc',
  templateUrl: './employee-kyc.component.html',
  styleUrls: ['./employee-kyc.component.scss']
})
export class EmployeeKycComponent implements OnInit {
  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;
  public kycs: DossierKYC[] = [];
  public isLoading: boolean = true;
  public submitted: boolean = true;
  public searchKeyword: string = "";
  public privilege: PrivilegeType;
  private context: Context;
  selectedFile: File | null = null;
  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private commonService: CommonService,
    private dossierService: DossierKycService,
    private dataService: DataService
  ) { }

  ngOnInit(): void {
    this.dataService.privilege().subscribe(privilege => { this.privilege = this.commonService.getPrivileges(privilege) });
    this.context = this.authService.getContextData();
    if (this.context && this.context?.employeeId) {
      this.getKycs();
    }
  }

  private getKycs(clear: boolean = true) {
    if (this.context?.employeeId) {
      if (clear) {
        this.isLoading = true;
        this.kycs = [];
      }
      this.dossierService.getKycs(this.context.employeeId).subscribe({
        next: (kyc: DossierKYC[]) => {
          this.kycs = kyc;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
    } else {
      this.isLoading = false;
    }
  }

  public upsertkyc(kyc: DossierKYC) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(ImagePreviewPopupComponent, { size: 'md', centered: true });
    modalRef.componentInstance.documentUrl = kyc.documentFileUrl;
    modalRef.componentInstance.type = kyc.type;
  }

  public saveKycFromRow(kyc: DossierKYC): void {
    const inProgress = { value: true };
    const currentDate = new Date().toISOString();
    if (kyc.id) {
      this.dossierService.updateKyc(kyc.id, {
        ...kyc,
        employeeId: this.context.employeeId,
        status: 'PENDING',
        submittedDate: currentDate
      }).subscribe({
        next: (response: Message) => {
          this.commonService.setToastr(200, response.text);
          if (this.selectedFile) {
            this.uploadDocument(kyc.id, this.selectedFile);
          }
        },
        error: (error: HttpErrorResponse) => {
          inProgress.value = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
    } else {
      this.dossierService.createKyc({
        ...kyc,
        employeeId: this.context.employeeId,
        status: 'PENDING',
        submittedDate: currentDate
      }).subscribe({
        next: (response: Message) => {
          this.commonService.setToastr(200, response.text);
          if (response.payload && this.selectedFile) {
            this.uploadDocument(response.payload, this.selectedFile);
          }
        },
        error: (error: HttpErrorResponse) => {
          inProgress.value = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
    }
  }

  private uploadDocument(kycId: number, file: File): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kycId', kycId.toString());

    this.dossierService.uploadKyc(kycId, formData).subscribe({
      next: () => {
        this.getKycs();
        this.commonService.setToastr(200, 'Document uploaded successfully!');
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  onFileSelected(event: Event, kyc: DossierKYC): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']; 
  
      if (!allowedTypes.includes(file.type)) {
        this.commonService.setToastr(400, 'Only JPEG, PNG, and PDF files are allowed.');
        return;
      }
  
      this.selectedFile = file;
      this.saveKycFromRow(kyc);
    }
  }
}
